import React, { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BeatLoader } from 'react-spinners';
import RestService from '../Services/RestService';
import OKomLoading from '../Components/OsloKommuneUI/OKomLoading';
import OKomBreadcrumb from '../Components/OsloKommuneUI/OKomBreadcrumb';
import myconfig from '../Config';

type RowKey =
  | 'Firstname' | 'Lastname'
  | 'Gender' | 'DateOfBirth' | 'DepartmentName' | 'Events'
  | 'Parent1Email' | 'Parent1Mobile' | 'Parent2Email' | 'Parent2Mobile'
  | 'DateCreated';

const COLUMNS: { key: RowKey; label: string }[] = [
  { key: 'Firstname',      label: 'Fornavn' },
  { key: 'Lastname',       label: 'Etternavn' },
  { key: 'Gender',         label: 'Kjønn' },
  { key: 'DateOfBirth',    label: 'Fødselsdato' },
  { key: 'DepartmentName', label: 'Avdeling' },
  { key: 'Events',         label: 'Øvelser' },
  { key: 'Parent1Email',   label: 'Forelder e-post' },
  { key: 'Parent1Mobile',  label: 'Forelder mobil' },
  { key: 'Parent2Email',   label: 'Forelder 2 e-post' },
  { key: 'Parent2Mobile',  label: 'Forelder 2 mobil' },
  { key: 'DateCreated',    label: 'Registrert' },
];

const genderLabel = (code?: string): string =>
  code === 'boy' ? 'Gutt' : code === 'girl' ? 'Jente' : '';

const formatDob = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}.${mm}.${d.getFullYear()}`;
};

const formatDateTime = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}.${mm}.${d.getFullYear()} ${hh}:${mi}`;
};

const yearOf = (iso?: string): number | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d.getFullYear();
};

const tableStyle: React.CSSProperties = { borderCollapse: 'collapse', fontSize: '0.875rem', width: '100%' };
const thStyle: React.CSSProperties = { border: '1px solid #ccc', padding: '6px 8px', background: '#f0f0f0', textAlign: 'left', position: 'sticky', top: 0 };
const tdStyle: React.CSSProperties = { border: '1px solid #ccc', padding: '6px 8px' };
const filterRowStyle: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'flex-end' };
const filterLabelStyle: React.CSSProperties = { minWidth: 180, flex: '0 0 auto' };

const AthleticsRegistrationReportView: React.FC<RouteComponentProps> = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState<Api.AthleticsRegistrationDto[]>([]);
  const [departments, setDepartments] = useState<Api.Department[]>([]);
  const [events, setEvents] = useState<Api.AthleticsEventDto[]>([]);

  const [deptFilter, setDeptFilter] = useState<string>('');
  const [yearFilter, setYearFilter] = useState<string>('');
  const [eventFilter, setEventFilter] = useState<string>('');
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const [data, depts, evts] = await Promise.all([
          RestService.get<Api.AthleticsRegistrationDto[]>('/api/athleticsregistration/All'),
          RestService.get<Api.Department[]>('/api/examregistration/GetDepartments'),
          RestService.get<Api.AthleticsEventDto[]>('/api/athleticsregistration/GetEvents'),
        ]);
        setRows(data ?? []);
        setDepartments(depts ?? []);
        setEvents(evts ?? []);
      } catch (e) {
        toast.error(`Kunne ikke laste rapport: ${e}`);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const departmentName = (id?: number) => departments.find(d => d.Id === id)?.Name ?? '';
  const eventNameMap = useMemo(() => new Map(events.map(e => [e.Id, e.NameNo])), [events]);
  const eventsLabel = (ids: number[]) =>
    (ids ?? [])
      .map(id => eventNameMap.get(id))
      .filter((n): n is string => !!n)
      .join(', ');

  // Distinct filter options derived from the data so dropdowns never offer zero-result picks.
  const deptOptions = useMemo(() => {
    const ids = new Set(rows.map(r => r.DepartmentId).filter((v): v is number => v != null && v > 0));
    return departments
      .filter(d => ids.has(d.Id))
      .sort((a, b) => a.Name.localeCompare(b.Name, 'nb'));
  }, [rows, departments]);

  const yearOptions = useMemo(() => {
    const years = new Set<number>();
    rows.forEach(r => {
      const y = yearOf(r.DateOfBirth);
      if (y != null) years.add(y);
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [rows]);

  const eventOptions = useMemo(() => {
    const ids = new Set<number>();
    rows.forEach(r => (r.EventIds ?? []).forEach(id => ids.add(id)));
    return events
      .filter(e => ids.has(e.Id))
      .sort((a, b) => a.NameNo.localeCompare(b.NameNo, 'nb'));
  }, [rows, events]);

  const filteredRows = useMemo(() => {
    const eventId = eventFilter ? parseInt(eventFilter, 10) : null;
    return rows.filter(r => {
      if (deptFilter && String(r.DepartmentId ?? '') !== deptFilter) return false;
      if (yearFilter && String(yearOf(r.DateOfBirth) ?? '') !== yearFilter) return false;
      if (eventId != null && !(r.EventIds ?? []).includes(eventId)) return false;
      return true;
    });
  }, [rows, deptFilter, yearFilter, eventFilter]);

  const cellValue = (row: Api.AthleticsRegistrationDto, key: RowKey): string => {
    switch (key) {
      case 'DepartmentName': return departmentName(row.DepartmentId);
      case 'DateOfBirth':    return formatDob(row.DateOfBirth);
      case 'DateCreated':    return formatDateTime(row.DateCreated);
      case 'Events':         return eventsLabel(row.EventIds);
      case 'Gender':         return genderLabel(row.Gender);
      default: {
        const v = (row as any)[key];
        return v == null ? '' : String(v);
      }
    }
  };

  const exportXlsx = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const token = localStorage.getItem('myToken');
      const response = await fetch(`${myconfig.rootUrl}/api/athleticsregistration/ExportXlsx`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error(`Error: ${response.status}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const ts = new Date().toISOString().replace(/[:T]/g, '-').slice(0, 16);
      const a = document.createElement('a');
      a.href = url;
      a.download = `friidrettsregistrering-${ts}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`Kunne ikke eksportere: ${e}`);
    } finally {
      setIsExporting(false);
    }
  };

  const hasActiveFilter = !!(deptFilter || yearFilter || eventFilter);
  const countLabel = hasActiveFilter ? `${filteredRows.length}/${rows.length}` : `${rows.length}`;

  return (
    <>
      <OKomBreadcrumb path='Rapporter, Friidrettsregistrering'></OKomBreadcrumb>
      {isLoading && <OKomLoading text='Henter rapport...' />}
      <div className="ods-margin-top-25 ods-grid ods-grid--gap ods-grid--center" style={{ maxWidth: '95rem' }}>
        <div className="ods-grid__column--12">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
            <h2 style={{ margin: 0 }}>Friidrettsregistrering — rapport ({countLabel})</h2>
            <button
              type="button"
              className="ods-button"
              onClick={exportXlsx}
              disabled={rows.length === 0 || isExporting}
            >
              Eksporter til Excel
              {isExporting && (
                <span className="ods-margin-left-1" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                  <BeatLoader color="#ffffff" size={8} />
                </span>
              )}
            </button>
          </div>
        </div>

        <div className="ods-grid__column--12 ods-margin-top-3">
          <div style={filterRowStyle}>
            <label className="ods-input__label" style={filterLabelStyle}>Avdeling
              <div className="ods-select__select">
                <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)}>
                  <option value="">Alle avdelinger</option>
                  {deptOptions.map(d => (<option key={d.Id} value={d.Id}>{d.Name}</option>))}
                </select>
              </div>
            </label>

            <label className="ods-input__label" style={filterLabelStyle}>Fødselsår
              <div className="ods-select__select">
                <select value={yearFilter} onChange={e => setYearFilter(e.target.value)}>
                  <option value="">Alle år</option>
                  {yearOptions.map(y => (<option key={y} value={y}>{y}</option>))}
                </select>
              </div>
            </label>

            <label className="ods-input__label" style={filterLabelStyle}>Øvelse
              <div className="ods-select__select">
                <select value={eventFilter} onChange={e => setEventFilter(e.target.value)}>
                  <option value="">Alle øvelser</option>
                  {eventOptions.map(ev => (<option key={ev.Id} value={ev.Id}>{ev.NameNo}</option>))}
                </select>
              </div>
            </label>

            {hasActiveFilter && (
              <button
                type="button"
                className="ods-button ods-button--red"
                onClick={() => { setDeptFilter(''); setYearFilter(''); setEventFilter(''); }}
              >
                Nullstill filter
              </button>
            )}
          </div>
        </div>

        <div className="ods-grid__column--12 ods-margin-top-3" style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                {COLUMNS.map(c => (<th key={c.key} style={thStyle}>{c.label}</th>))}
              </tr>
            </thead>
            <tbody>
              {filteredRows.map(r => (
                <tr key={r.Id}>
                  {COLUMNS.map(c => (<td key={c.key} style={tdStyle}>{cellValue(r, c.key)}</td>))}
                </tr>
              ))}
              {filteredRows.length === 0 && !isLoading && (
                <tr>
                  <td style={tdStyle} colSpan={COLUMNS.length}>
                    {rows.length === 0 ? 'Ingen registreringer.' : 'Ingen treff for valgte filter.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default withRouter(AthleticsRegistrationReportView);
